import React, { useEffect, useState, useCallback, useRef } from "react";
import "./DataVisualization.css";
import Pathology from "./images/Pathology Reports.svg";
import Medical from "./images/Medical Images.svg";
import Vaccination from "./images/Vaccination.svg";
import Prescriptions from "./images/Prescription.svg";
import Hospital from "./images/Hospital Records.svg";
import Expenses from "./images/Medical Expenses.svg";
import Chart from "react-apexcharts";
import { useApp } from "../../../AppProvider";
import api from "./../../../axiosInstance";
import logger from "../../../utils/logger";

const SERIES_COLORS = [
  "#E8941A", "#C0392B", "#1F5FA8", "#247A8B",
  "#1F7A45", "#7C3AED", "#B83A7A", "#0F766E",
  "#D97706", "#DC2626", "#2563EB", "#059669",
];

const GLUCOSE_TYPE_DEFS = [
  { key: "Before Meal", label: "Glucose · Before Meal", color: "#1F5FA8" },
  { key: "After Meal", label: "Glucose · After Meal", color: "#C0392B" },
  { key: "Random", label: "Glucose · Random", color: "#1F7A45" },
];

const KNOWN_GLUCOSE_TIMES = new Set(["Before Meal", "After Meal"]);

const RANGE_OPTIONS = ["1M", "3M", "6M", "1Y", "All"];

// Preset params pinned at the top of the sidebar with display metadata.
// All use testName= in the API, same as canonical lab tests.
const PRESET_PARAMS = {
  glucose: { id: "glucose", label: "Glucose", unit: "mg/dL" },
  bloodPressure: { id: "bloodPressure", label: "Blood Pressure", unit: "mmHg", isBP: true },
  weight: { id: "weight", label: "Weight", unit: "kg" },
};

const PRESET_PARAM_LIST = Object.values(PRESET_PARAMS);

const CLINICAL_PANELS = [
  { id: "diabetes", label: "Diabetes", subtitle: "Glucose · HbA1c · Wt", params: ["glucose", "HbA1c", "weight"] },
  { id: "htn", label: "Hypertension", subtitle: "BP · Weight", params: ["bloodPressure", "weight"] },
  { id: "lipid", label: "Lipid Profile", subtitle: "LDL · HDL · TG", params: ["LDL Cholesterol", "HDL", "Triglycerides"] },
  { id: "renal", label: "Renal", subtitle: "Creat · eGFR · K", params: ["Creatinine", "eGFR", "Potassium"] },
];

const DEFAULT_RANGE_MONTHS = 36; // fallback when a range has no explicit month count (e.g. "All")

const getRangeDates = (rangeLabel) => {
  const months = { "1M": 1, "3M": 3, "6M": 6, "1Y": 12 }[rangeLabel] ?? DEFAULT_RANGE_MONTHS;
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - months);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const DataVisualization = () => {
  const { selectedPatientObj, patientsMHR } = useApp();
  const [canonicalData, setCanonicalData] = useState([]);
  const [selectedParams, setSelectedParams] = useState([]);
  const [seriesData, setSeriesData] = useState({});
  const [selectedRange, setSelectedRange] = useState("6M");
  const [viewMode, setViewMode] = useState("line");
  const [paramSearch, setParamSearch] = useState("");
  const [loadingParams, setLoadingParams] = useState({});
  const [activePanel, setActivePanel] = useState(null);

  const patientId = selectedPatientObj?._id;
  const safeData = Array.isArray(patientsMHR) ? patientsMHR : [];

  const counts = {
    pathology: safeData.filter(i => i.typeOfRecord === "PathologyRecord").length,
    imaging: safeData.filter(i => i.typeOfRecord === "MedicalImageRecord").length,
    vaccines: safeData.filter(i => i.typeOfRecord === "VaccinationRecord").length,
    prescriptions: safeData.filter(i => i.typeOfRecord === "PrescriptionRecord").length,
    hospital: safeData.filter(i => i.typeOfRecord === "HospitalRecords").length,
    expenses: safeData.filter(i => i.typeOfRecord === "MedicalExpenses").length,
  };

  const fetchParamData = useCallback(async (paramId, range) => {
    if (!patientId) return;
    setLoadingParams(prev => ({ ...prev, [paramId]: true }));
    try {
      const { startDate, endDate } = getRangeDates(range);
      let url = `/patients/getTestValues?testName=${encodeURIComponent(paramId)}&patientId=${patientId}`;
      if (startDate && endDate) url += `&startDate=${startDate}&endDate=${endDate}`;
      const response = await api.get(url);
      setSeriesData(prev => ({ ...prev, [paramId]: response.data }));
    } catch (err) {
      logger.error("Error fetching data for", paramId, err);
    } finally {
      setLoadingParams(prev => ({ ...prev, [paramId]: false }));
    }
  }, [patientId]);

  useEffect(() => {
    api.get(`/testName/canonicalnames`)
      .then(res => setCanonicalData(res.data.map(item => ({ label: item, value: item }))))
      .catch(err => logger.error("Error fetching canonical names:", err));
  }, []);

  const prevRange = useRef(selectedRange);
  useEffect(() => {
    if (prevRange.current === selectedRange) return;
    prevRange.current = selectedRange;
    selectedParams.forEach(id => fetchParamData(id, selectedRange));
  }, [selectedRange, selectedParams, fetchParamData]);

  const toggleParam = (paramId) => {
    setActivePanel(null);
    setSelectedParams(prev => {
      if (prev.includes(paramId)) {
        setSeriesData(sd => { const n = { ...sd }; delete n[paramId]; return n; });
        return prev.filter(p => p !== paramId);
      }
      fetchParamData(paramId, selectedRange);
      return [...prev, paramId];
    });
  };

  const selectPanel = (panel) => {
    setActivePanel(panel.id);
    const canonicalSet = new Set(canonicalData.map(c => c.value));
    const toSelect = panel.params.filter(p => PRESET_PARAMS[p] || canonicalSet.has(p));
    setSeriesData({});
    setSelectedParams(toSelect);
    toSelect.forEach(id => fetchParamData(id, selectedRange));
  };

  // ---- Build ApexCharts series (BP expands to 2 series) ----
  const apexSeries = [];
  const paramColorMap = {}; // paramId → primary color (for chips)
  let colorCursor = 0;
  const glucoseDiscreteMarkers = []; // discrete per-point colors for glucose
  const glucoseTypesWithData = new Set(); // which whenMeasured types have data

  selectedParams.forEach(paramId => {
    const raw = seriesData[paramId];
    const values = Array.isArray(raw?.values) ? raw.values : [];
    const phrConfig = PRESET_PARAMS[paramId];
    const sorted = [...values].filter(v => v?.date).sort((a, b) => new Date(a.date) - new Date(b.date));

    if (phrConfig?.isBP) {
      const hasSysDia = sorted.length > 0 && ("systolic" in sorted[0] || "diastolic" in sorted[0]);
      const sysColor = SERIES_COLORS[colorCursor % SERIES_COLORS.length];
      const diaColor = SERIES_COLORS[(colorCursor + 1) % SERIES_COLORS.length];
      paramColorMap[paramId] = sysColor;

      apexSeries.push({
        name: "Systolic BP",
        color: sysColor,
        data: sorted.map(v => ({
          x: new Date(v.date).getTime(),
          y: hasSysDia ? parseFloat(v.systolic) : parseFloat(String(v.value).split("/")[0]),
        })).filter(p => !isNaN(p.y)),
      });
      apexSeries.push({
        name: "Diastolic BP",
        color: diaColor,
        data: sorted.map(v => ({
          x: new Date(v.date).getTime(),
          y: hasSysDia ? parseFloat(v.diastolic) : parseFloat(String(v.value).split("/")[1]),
        })).filter(p => !isNaN(p.y)),
      });
      colorCursor += 2;
    } else if (paramId === "glucose") {
      const color = SERIES_COLORS[colorCursor % SERIES_COLORS.length];
      paramColorMap[paramId] = color;
      const seriesIdx = apexSeries.length;
      const pts = sorted
        .map(v => ({ x: new Date(v.date).getTime(), y: parseFloat(v.value), whenMeasured: v.whenMeasured }))
        .filter(p => !isNaN(p.y));
      pts.forEach((pt, ptIdx) => {
        const typeKey = GLUCOSE_TYPE_DEFS.find(t => t.key === pt.whenMeasured)?.key ?? "Random";
        glucoseTypesWithData.add(typeKey);
        glucoseDiscreteMarkers.push({
          seriesIndex: seriesIdx,
          dataPointIndex: ptIdx,
          fillColor: GLUCOSE_TYPE_DEFS.find(t => t.key === typeKey).color,
          strokeColor: "#fff",
          size: viewMode === "dots" ? 6 : 4,
        });
      });
      apexSeries.push({ name: "Glucose", color, data: pts.map(p => ({ x: p.x, y: p.y })) });
      colorCursor++;
    } else {
      const color = SERIES_COLORS[colorCursor % SERIES_COLORS.length];
      paramColorMap[paramId] = color;
      apexSeries.push({
        name: phrConfig?.label || paramId,
        color,
        data: sorted.map(v => ({ x: new Date(v.date).getTime(), y: parseFloat(v.value) })).filter(p => !isNaN(p.y)),
      });
      colorCursor++;
    }
  });

  // ---- Y-axis config aligned to apexSeries ----
  // Diastolic BP shares Systolic BP's axis so both use the same scale.
  const yaxisConfig = apexSeries.length <= 1
    ? [{ labels: { style: { colors: "#64748B", fontSize: "11px" }, formatter: v => v != null ? v.toFixed(1) : "" } }]
    : apexSeries.map((s, i) => {
      if (s.name === "Diastolic BP") {
        return { seriesName: "Systolic BP", show: false };
      }
      return {
        seriesName: s.name,
        opposite: i % 2 !== 0,
        labels: { style: { colors: s.color, fontSize: "10.5px", fontWeight: "600" }, formatter: v => v != null ? v.toFixed(1) : "" },
        title: { text: s.name, style: { color: s.color, fontSize: "10px", fontWeight: "600" } },
      };
    });

  const chartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: true, speed: 400 }, background: "transparent" },
    stroke: { curve: "smooth", width: viewMode === "dots" ? 0 : 2.25 },
    markers: { size: viewMode === "dots" ? 6 : 3.5, strokeWidth: 1.5, strokeColors: "#fff", hover: { size: 7 }, colors: apexSeries.map(s => s.color), discrete: glucoseDiscreteMarkers },
    colors: apexSeries.map(s => s.color),
    xaxis: {
      type: "datetime",
      labels: { datetimeFormatter: { month: "MMM 'yy", day: "dd MMM" }, style: { colors: "#64748B", fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: yaxisConfig,
    grid: { borderColor: "#EEF1F6", strokeDashArray: 0 },
    legend: { show: false },
    tooltip: { theme: "dark", x: { format: "dd MMM yyyy" }, style: { fontSize: "12px" } },
    dataLabels: { enabled: false },
  };

  // ---- Latest values table (BP expands to 2 rows) ----
  const latestRows = [];
  let latestColorCursor = 0;

  selectedParams.forEach(paramId => {
    const raw = seriesData[paramId];
    const values = Array.isArray(raw?.values) ? raw.values : [];
    const phrConfig = PRESET_PARAMS[paramId];
    const sorted = [...values].filter(v => v?.date).sort((a, b) => new Date(b.date) - new Date(a.date));
    const latest = sorted[0];
    const prev = sorted[1];
    // Unit comes from the response; fall back to PRESET_PARAMS or empty string
    const unit = latest?.unit || phrConfig?.unit || "";

    if (phrConfig?.isBP) {
      const hasSysDia = latest && ("systolic" in latest || "diastolic" in latest);
      const getVal = (item, idx) => {
        if (!item) return null;
        if (hasSysDia) return parseFloat(idx === 0 ? item.systolic : item.diastolic);
        const parts = String(item.value).split("/");
        return parseFloat(parts[idx]);
      };
      latestRows.push({ name: "Systolic BP", unit, color: SERIES_COLORS[latestColorCursor++ % SERIES_COLORS.length], latestVal: getVal(latest, 0), latestDate: latest?.date ?? null, prevVal: getVal(prev, 0) });
      latestRows.push({ name: "Diastolic BP", unit, color: SERIES_COLORS[latestColorCursor++ % SERIES_COLORS.length], latestVal: getVal(latest, 1), latestDate: latest?.date ?? null, prevVal: getVal(prev, 1) });
    } else if (paramId === "glucose") {
      GLUCOSE_TYPE_DEFS.forEach(typeDef => {
        const ts = sorted.filter(v => typeDef.key === "Random"
          ? (!v.whenMeasured || v.whenMeasured === "Random" || !KNOWN_GLUCOSE_TIMES.has(v.whenMeasured))
          : v.whenMeasured === typeDef.key);
        const tLatest = ts[0];
        const tPrev = ts[1];
        if (!tLatest) return;
        latestRows.push({
          name: typeDef.label,
          unit: tLatest?.unit || phrConfig?.unit || "",
          color: typeDef.color,
          latestVal: parseFloat(tLatest.value),
          latestDate: tLatest?.date ?? null,
          prevVal: tPrev ? parseFloat(tPrev.value) : null,
        });
      });
      latestColorCursor += 1; // glucose is 1 series slot (single line)
    } else {
      latestRows.push({
        name: phrConfig?.label || paramId,
        unit,
        color: SERIES_COLORS[latestColorCursor++ % SERIES_COLORS.length],
        latestVal: latest ? parseFloat(latest.value) : null,
        latestDate: latest?.date ?? null,
        prevVal: prev ? parseFloat(prev.value) : null,
      });
    }
  });

  // ---- Sidebar param list: PHR (Personal Health) + Canonical (Lab Tests) ----
  const allParams = [
    ...PRESET_PARAM_LIST.map(p => ({ ...p, isPhr: true })),
    ...canonicalData.map(c => ({ id: c.value, label: c.label, isPhr: false })),
  ];
  const filteredParams = allParams.filter(p => p.label.toLowerCase().includes(paramSearch.toLowerCase()));
  const filteredPhr = filteredParams.filter(p => p.isPhr);
  const filteredCanonical = filteredParams.filter(p => !p.isPhr);

  const fmtDate = iso => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });

  const renderParamRow = (p) => {
    const isChecked = selectedParams.includes(p.id);
    const color = isChecked ? (paramColorMap[p.id] || "#CBD5E1") : "#CBD5E1";
    const raw = seriesData[p.id];
    const values = Array.isArray(raw?.values) ? raw.values : [];
    const sortedV = [...values].filter(v => v?.date).sort((a, b) => new Date(b.date) - new Date(a.date));
    const latest = sortedV[0];
    let displayVal = null;
    let whenMeasured = null;
    if (latest) {
      if (p.isBP) {
        const hasSysDia = "systolic" in latest || "diastolic" in latest;
        displayVal = hasSysDia ? `${latest.systolic}/${latest.diastolic}` : latest.value;
      } else {
        const n = parseFloat(latest.value);
        displayVal = isNaN(n) ? null : n;
      }
      if (p.id === "glucose" && latest.whenMeasured) whenMeasured = latest.whenMeasured;
    }
    return (
      <label key={p.id} className="dv-param">
        <input type="checkbox" checked={isChecked} onChange={() => toggleParam(p.id)} />
        <span className="dv-swatch" style={{ background: color }} />
        <span className="dv-param-name">{p.label}</span>
        {loadingParams[p.id] && <span className="dv-spinner" />}
        {!loadingParams[p.id] && displayVal !== null && (
          <span className="dv-param-latest">
            {displayVal}
            {whenMeasured && <span className="dv-when-measured">{whenMeasured}</span>}
          </span>
        )}
      </label>
    );
  };

  return (
    <div className="dv-container">
      {/* Records strip */}
      {/* <div className="dv-strip">
        {[
          { icon: Pathology, label: "Pathology", count: counts.pathology },
          { icon: Medical, label: "Imaging", count: counts.imaging },
          { icon: Vaccination, label: "Vaccines", count: counts.vaccines },
          { icon: Prescriptions, label: "Prescriptions", count: counts.prescriptions },
          { icon: Hospital, label: "Admissions", count: counts.hospital },
          { icon: Expenses, label: "Expenses", count: counts.expenses },
        ].map(({ icon, label, count }) => (
          <div key={label} className="dv-scell">
            <img src={icon} alt={label} className="dv-scell-icon" />
            <span className="dv-scell-label">{label}</span>
            <span className="dv-scell-num">{count}</span>
          </div>
        ))}
      </div> */}
      <div className="dv-work">
        {/* Sidebar */}
        <aside className="dv-sidebar">
          {/* Clinical Panels */}
          <div className="dv-panel">
            <div className="dv-ph">Clinical Panels</div>
            <div className="dv-pb">
              <div className="dv-panels-grid">
                {CLINICAL_PANELS.map(panel => (
                  <button
                    key={panel.id}
                    className={`dv-panel-btn${activePanel === panel.id ? " on" : ""}`}
                    onClick={() => selectPanel(panel)}
                  >
                    <span className="dv-panel-btn-label">{panel.label}</span>
                    <span className="dv-panel-btn-sub">{panel.subtitle}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Parameters */}
          <div className="dv-panel">
            <div className="dv-ph">
              Parameters
              <span className="dv-ph-meta">{selectedParams.length} selected</span>
            </div>
            <div className="dv-pb">
              <input
                className="dv-search"
                placeholder="Search…"
                value={paramSearch}
                onChange={e => setParamSearch(e.target.value)}
              />
              <div className="dv-param-list">
                {filteredPhr.length > 0 && (
                  <>
                    <div className="dv-group-title">Personal Health</div>
                    {filteredPhr.map(renderParamRow)}
                  </>
                )}
                {filteredCanonical.length > 0 && (
                  <>
                    <div className="dv-group-title">Lab Tests</div>
                    {filteredCanonical.map(renderParamRow)}
                  </>
                )}
                {filteredParams.length === 0 && (
                  <div className="dv-empty">
                    {canonicalData.length === 0 ? "Loading…" : "No parameters found"}
                  </div>
                )}
              </div>
            </div>
          </div>

        </aside>

        {/* Main */}
        <section className="dv-main">

          <div className="dv-panel dv-chart-card">
            <div className="dv-ph">
              Trend over time
              <div className="dv-seg">
                {RANGE_OPTIONS.map(r => (
                  <button key={r} className={selectedRange === r ? "on" : ""} onClick={() => setSelectedRange(r)}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="dv-toolbar">
              <div className="dv-chips">
                {selectedParams.map(paramId => {
                  const label = PRESET_PARAMS[paramId]?.label || paramId;
                  const color = paramColorMap[paramId] || "#CBD5E1";
                  return (
                    <span key={paramId} className="dv-chip">
                      <span className="dv-chip-dot" style={{ background: color }} />
                      {label}
                      <span className="dv-chip-x" onClick={() => toggleParam(paramId)}>×</span>
                    </span>
                  );
                })}
                {selectedParams.length === 0 && (
                  <span className="dv-chips-hint">Select parameters from the sidebar</span>
                )}
              </div>
              {selectedParams.includes("glucose") && glucoseTypesWithData.size > 0 && (
                <div className="dv-glucose-legend">
                  {GLUCOSE_TYPE_DEFS.filter(t => glucoseTypesWithData.has(t.key)).map(t => (
                    <span key={t.key} className="dv-glucose-legend-item">
                      <span className="dv-chip-dot" style={{ background: t.color }} />
                      {t.key}
                    </span>
                  ))}
                </div>
              )}
              <div className="dv-seg">
                <button className={viewMode === "line" ? "on" : ""} onClick={() => setViewMode("line")}>Line</button>
                <button className={viewMode === "dots" ? "on" : ""} onClick={() => setViewMode("dots")}>Dots</button>
              </div>
            </div>

            <div className="dv-chart-wrap">
              {selectedParams.length === 0 ? (
                <div className="dv-chart-empty">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  <p>Select a parameter from the sidebar to view trends</p>
                </div>
              ) : (
                <Chart options={chartOptions} series={apexSeries} type="line" height={360} />
              )}
            </div>
          </div>

          {selectedParams.length > 0 && (
            <div className="dv-panel dv-values">
              <div className="dv-ph">
                Latest Values
                <span className="dv-ph-meta">vs. previous reading</span>
              </div>
              <table className="dv-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Latest</th>
                    <th>Date</th>
                    <th>Δ Change</th>
                  </tr>
                </thead>
                <tbody>
                  {latestRows.map((row, idx) => {
                    const noData = row.latestVal === null || isNaN(row.latestVal);
                    if (noData) return (
                      <tr key={idx}>
                        <td>
                          <span className="dv-pname-cell">
                            <span className="dv-sw" style={{ background: row.color }} />
                            <span className="dv-nm">{row.name}</span>
                            {row.unit && <span className="dv-unit">{row.unit}</span>}
                          </span>
                        </td>
                        <td colSpan={3} className="dv-no-data">No data available</td>
                      </tr>
                    );
                    const delta = row.prevVal != null && !isNaN(row.prevVal) ? +(row.latestVal - row.prevVal).toFixed(2) : null;
                    const arrow = delta === null ? "—" : delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
                    const deltaClass = `dv-delta${delta === null ? "" : delta > 0 ? " up" : delta < 0 ? " dn" : ""}`;
                    return (
                      <tr key={idx}>
                        <td>
                          <span className="dv-pname-cell">
                            <span className="dv-sw" style={{ background: row.color }} />
                            <span className="dv-nm">{row.name}</span>
                            {row.unit && <span className="dv-unit">{row.unit}</span>}
                          </span>
                        </td>
                        <td><span className="dv-val">{row.latestVal}</span></td>
                        <td><span className="dv-when">{fmtDate(row.latestDate)}</span></td>
                        <td>
                          <span className={deltaClass}>{arrow}{delta !== null && delta !== 0 ? ` ${Math.abs(delta)}` : ""}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </section>
      </div>
    </div>
  );
};

export default DataVisualization;
